import { useState } from 'react'

import {
  SectionHeading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { cn } from '@/utils'
import { useProject, useSecuritiesReadState } from '../../hooks/project'
import { AccountsRollup } from './AccountsRollup'
import { AssetAllocation } from './AssetAllocation'
import { activityColumns, holdingsColumns, realizedColumns } from './columns'
import { MarketValueHero } from './MarketValueHero'
import { PortfolioFlowsChart } from './PortfolioFlowsChart'
import { PortfolioStatStrip } from './PortfolioStatStrip'
import { PortfolioTable } from './PortfolioTable'
import {
  activityFilterSchema,
  buildGetUniqueValues,
  holdingsFilterSchema,
  realizedFilterSchema,
} from './portfolioSchemas'
import { SecuritiesFailedState } from './SecuritiesFailedState'
import { SecuritiesPendingState } from './SecuritiesPendingState'
import type {
  ActivityRow,
  HoldingRow,
  PortfolioAccountSummary,
  RealizedRow,
} from './types'
import { snapshotTotals, usePortfolioDashboard } from './usePortfolioDashboard'

const LATEST = 'latest'

type TabKey = 'holdings' | 'activity' | 'realized'

const sum = <Row,>(rows: Row[], pick: (row: Row) => number): number =>
  rows.reduce((total, row) => total + pick(row), 0)

interface DashboardProps {
  slug: string
  accounts: PortfolioAccountSummary[]
  periods: { key: string; label: string }[]
  holdings: HoldingRow[]
  activity: ActivityRow[]
  realized: RealizedRow[]
}

// The brokerage dashboard: a header (KPIs, value chart, allocation, accounts table) above a tabbed
// set of filterable tables. The "as of" snapshot drives the holdings; every account stays in view.
const PortfolioDashboard = ({
  slug,
  accounts,
  periods,
  holdings,
  activity,
  realized,
}: DashboardProps) => {
  const [asOf, setAsOf] = useState<string>(LATEST)
  const [tab, setTab] = useState<TabKey>('holdings')

  const latestByAccount = new Map(
    accounts.map((account) => [account.id, account.latestPeriodKey]),
  )

  // Holdings are a point-in-time snapshot: "latest" takes each account's newest statement, a period
  // takes that month. Market value, allocation, and the Holdings tab all read this set. Activity and
  // realized gains are flow, so they run across every period.
  const snapshotHoldings = holdings.filter((holding) =>
    asOf === LATEST
      ? holding.period === latestByAccount.get(holding.account)
      : holding.period === asOf,
  )

  const { marketValue, costBasis, unrealized, unrealizedPercent } =
    snapshotTotals(snapshotHoldings)

  // Total portfolio value at each statement period, oldest first, for the hero sparkline.
  const valueByPeriod = new Map<string, number>()
  for (const holding of holdings) {
    valueByPeriod.set(
      holding.period,
      (valueByPeriod.get(holding.period) ?? 0) + (holding.marketValue ?? 0),
    )
  }
  const periodLabelByKey = new Map(
    periods.map((period) => [period.key, period.label]),
  )
  const valuePoints = Array.from(valueByPeriod.entries())
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([key, value]) => ({
      key,
      label: periodLabelByKey.get(key) ?? key,
      value,
    }))

  const reconciledCount = accounts.filter((account) => account.validated).length
  // A failed realized-gains check means the total below is short, so it can't be read as $0.00.
  const realizedNeedsReview = accounts.some((account) =>
    account.failingSections.some(
      (section) => section.section === 'realized_gains',
    ),
  )
  const asOfLabel =
    asOf === LATEST
      ? 'latest snapshot'
      : (periods.find((period) => period.key === asOf)?.label ?? asOf)

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'holdings', label: 'Holdings', count: snapshotHoldings.length },
    {
      key: 'activity',
      label: 'Activity (all periods)',
      count: activity.length,
    },
    {
      key: 'realized',
      label: 'Realized gains',
      count: realized.length,
    },
  ]

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      <p className="rounded-lg border bg-white px-4 py-3 text-sm text-ink-subtle">
        <span className="font-medium text-blue-7">Brokerage is in beta.</span>{' '}
        These numbers were verified by the system.
      </p>

      <SectionHeading
        title="Portfolio"
        className="flex-wrap items-center"
        actions={
          <>
            <span className="text-sm text-ink-subtle">Holdings as of</span>
            <Select value={asOf} onValueChange={setAsOf}>
              <SelectTrigger className="w-[180px] cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={LATEST}>Latest snapshot</SelectItem>
                {periods.map((period) => (
                  <SelectItem key={period.key} value={period.key}>
                    {period.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      <div className="@container">
        <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-[1.5fr_1fr]">
          <MarketValueHero
            marketValue={marketValue}
            unrealized={unrealized}
            unrealizedPercent={unrealizedPercent}
            costBasis={costBasis}
            asOfLabel={asOfLabel}
            valuePoints={valuePoints}
            selectedKey={asOf === LATEST ? undefined : asOf}
          />
          <AssetAllocation holdings={snapshotHoldings} />
        </div>
      </div>

      <AccountsRollup accounts={accounts} />

      <PortfolioStatStrip
        costBasis={costBasis}
        realized={sum(realized, (gain) => gain.gainLoss ?? 0)}
        realizedNeedsReview={realizedNeedsReview}
        holdingsCount={snapshotHoldings.length}
        accountCount={accounts.length}
        reconciledCount={reconciledCount}
      />

      <PortfolioFlowsChart activity={activity} realized={realized} />

      <div className="flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b">
        {tabs.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            className={cn(
              '-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium cursor-pointer',
              tab === entry.key
                ? 'border-blue-6 text-blue-7'
                : 'border-transparent text-muted-foreground hover:text-blue-7',
            )}
          >
            {entry.label}
            <span className="rounded-full bg-blue-1 px-1.5 text-xs tabular-nums text-blue-7">
              {entry.count}
            </span>
          </button>
        ))}
      </div>

      {tab === 'holdings' && (
        <PortfolioTable<HoldingRow>
          contextId={`project-${slug}-portfolio-holdings`}
          dialogId="portfolio-holdings"
          columns={holdingsColumns}
          data={snapshotHoldings}
          schema={holdingsFilterSchema}
          getUniqueValues={buildGetUniqueValues(snapshotHoldings, [
            'accountNumber',
            'assetClass',
          ])}
          exportFilename={`${slug}-holdings`}
          paginationKey={`${slug}-portfolio-holdings`}
          emptyMessage="No holdings for this snapshot."
        />
      )}
      {tab === 'activity' && (
        <PortfolioTable<ActivityRow>
          contextId={`project-${slug}-portfolio-activity`}
          dialogId="portfolio-activity"
          columns={activityColumns}
          data={activity}
          schema={activityFilterSchema}
          getUniqueValues={buildGetUniqueValues(activity, [
            'accountNumber',
            'type',
          ])}
          exportFilename={`${slug}-activity`}
          paginationKey={`${slug}-portfolio-activity`}
          emptyMessage="No activity yet."
        />
      )}
      {tab === 'realized' && (
        <PortfolioTable<RealizedRow>
          contextId={`project-${slug}-portfolio-realized`}
          dialogId="portfolio-realized"
          columns={realizedColumns}
          data={realized}
          schema={realizedFilterSchema}
          getUniqueValues={buildGetUniqueValues(realized, [
            'accountNumber',
            'term',
          ])}
          exportFilename={`${slug}-realized-gains`}
          paginationKey={`${slug}-portfolio-realized`}
          emptyMessage="No realized gains yet."
        />
      )}
    </div>
  )
}

// The tab shell. Renders the dashboard only when there is real data, else the empty state. Gating
// the inner component keeps its hooks unconditional.
export const PortfolioPanel = () => {
  const project = useProject()
  const securitiesReadState = useSecuritiesReadState()
  const { accounts, periods, holdings, activity, realized } =
    usePortfolioDashboard()

  if (accounts.length === 0) {
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-3">
        <SectionHeading title="Portfolio" />
        {securitiesReadState === 'reading' && (
          <SecuritiesPendingState
            layout="panel"
            detail="The system is calculating and verifying positions, activity, and realized gains. This usually takes up to 24 hours. They appear here when it's done."
          />
        )}
        {securitiesReadState === 'failed' && (
          <SecuritiesFailedState layout="panel" />
        )}
        {securitiesReadState !== 'reading' &&
          securitiesReadState !== 'failed' && (
            <div className="rounded-lg border bg-white p-10 text-center text-sm text-ink-subtle">
              No brokerage holdings yet. Process an investment or retirement
              statement to see positions, activity, and realized gains here.
            </div>
          )}
      </div>
    )
  }

  return (
    <PortfolioDashboard
      slug={project?.slug ?? 'portfolio'}
      accounts={accounts}
      periods={periods}
      holdings={holdings}
      activity={activity}
      realized={realized}
    />
  )
}
